import { CLUB_ZONE_FALLBACK } from '~/shared/lib/clubZone';

export type WallClock = string;

export type ClubDay = string;

export interface ClubNow {
  readonly zone: string;
  readonly instantMs: number;
  readonly wall: WallClock;
  readonly today: ClubDay;
  readonly minuteOfDay: number;
  readonly weekday: number;
}

const DAY_MS = 86_400_000;

const pad2 = (n: number): string => String(n).padStart(2, '0');

const formatters = new Map<string, { zone: string; format: Intl.DateTimeFormat }>();

const formatterFor = (zone: string): { zone: string; format: Intl.DateTimeFormat } => {
  const cached = formatters.get(zone);
  if (cached) return cached;
  let entry: { zone: string; format: Intl.DateTimeFormat };
  try {
    if (zone.trim() === '') throw new RangeError('пустой пояс');
    entry = {
      zone,
      format: new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }),
    };
  } catch {
    entry = zone === CLUB_ZONE_FALLBACK ? formatterFor('UTC') : formatterFor(CLUB_ZONE_FALLBACK);
  }
  formatters.set(zone, entry);
  return entry;
};

const dayPartsOf = (value: string): { year: number; month: number; day: number } | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
};

const wallDigitsOf = (value: string): WallClock | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? '00'}`;
};

const wallMsOf = (wall: WallClock): number => {
  const d = dayPartsOf(wall);
  const t = /T(\d{2}):(\d{2}):(\d{2})/.exec(wall);
  if (!d || !t) return Number.NaN;
  return Date.UTC(d.year, d.month - 1, d.day, Number(t[1]), Number(t[2]), Number(t[3]));
};

const dayKeyOf = (at: Date): ClubDay =>
  `${at.getUTCFullYear()}-${pad2(at.getUTCMonth() + 1)}-${pad2(at.getUTCDate())}`;

export const addDays = (day: ClubDay, offsetDays: number): ClubDay => {
  const p = dayPartsOf(day);
  if (!p) return day;
  return dayKeyOf(new Date(Date.UTC(p.year, p.month - 1, p.day + offsetDays)));
};

export const addMonths = (day: ClubDay, offsetMonths: number): ClubDay => {
  const p = dayPartsOf(day);
  if (!p) return day;
  const first = new Date(Date.UTC(p.year, p.month - 1 + offsetMonths, 1));
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return dayKeyOf(new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(p.day, lastDay))));
};

export function clubNowIn(zone: string, at: Date | number = Date.now()): ClubNow {
  const instantMs = typeof at === 'number' ? at : at.getTime();
  const { zone: effective, format } = formatterFor(zone);
  if (!Number.isFinite(instantMs)) {
    return {
      zone: effective,
      instantMs: Number.NaN,
      wall: '',
      today: '',
      minuteOfDay: Number.NaN,
      weekday: Number.NaN,
    };
  }
  const parts = format.formatToParts(instantMs);
  const pick = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? Number.NaN);
  const year = pick('year');
  const month = pick('month');
  const day = pick('day');
  const hour = pick('hour') % 24;
  const minute = pick('minute');
  const second = pick('second');
  const today = `${year}-${pad2(month)}-${pad2(day)}`;
  return {
    zone: effective,
    instantMs,
    wall: `${today}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}`,
    today,
    minuteOfDay: hour * 60 + minute,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

export function clubDayFromToday(offsetDays: number, now: ClubNow): ClubDay {
  return addDays(now.today, offsetDays);
}

export function clubDaysUntil(day: string, now: ClubNow): number {
  const target = dayPartsOf(day);
  const today = dayPartsOf(now.today);
  if (!target || !today) return Number.NaN;
  return Math.round(
    (Date.UTC(target.year, target.month - 1, target.day) -
      Date.UTC(today.year, today.month - 1, today.day)) /
      DAY_MS,
  );
}

export function isClubToday(day: string, now: ClubNow): boolean {
  return clubDaysUntil(day, now) === 0;
}

export function isClubDayPast(day: string, now: ClubNow): boolean {
  return clubDaysUntil(day, now) < 0;
}

export function wallClockOf(date: string, time: string): WallClock {
  const hhmmss = /^\d{2}:\d{2}$/.test(time) ? `${time}:00` : time.slice(0, 8);
  return `${date.slice(0, 10)}T${hhmmss}`;
}

export function isWallClockPast(value: string, now: ClubNow): boolean {
  const wall = wallDigitsOf(value);
  return wall !== null && now.wall !== '' && wall <= now.wall;
}

export function isWallClockLive(start: string, end: string, now: ClubNow): boolean {
  const from = wallDigitsOf(start);
  const to = wallDigitsOf(end);
  return from !== null && to !== null && now.wall !== '' && from <= now.wall && now.wall < to;
}

export function minutesUntilWallClock(value: string, now: ClubNow): number {
  const wall = wallDigitsOf(value);
  if (wall === null || now.wall === '') return Number.NaN;
  return (wallMsOf(wall) - wallMsOf(now.wall)) / 60_000;
}

export function hoursUntilWallClock(value: string, now: ClubNow): number {
  return minutesUntilWallClock(value, now) / 60;
}

export function wallClockToInstant(value: string, zone: string): number {
  const wall = wallDigitsOf(value);
  if (wall === null) return Number.NaN;
  const asUtc = wallMsOf(wall);
  let guess = asUtc;
  for (let pass = 0; pass < 2; pass++) {
    const offset = wallMsOf(clubNowIn(zone, guess).wall) - guess;
    guess = asUtc - offset;
  }
  return guess;
}

export function msUntilNextClubMidnight(now: ClubNow): number {
  if (now.today === '' || !Number.isFinite(now.instantMs)) return Number.NaN;
  const midnight = wallMsOf(`${addDays(now.today, 1)}T00:00:00`);
  const secondMs = Math.floor(now.instantMs / 1000) * 1000;
  const offsetNow = wallMsOf(now.wall) - secondMs;
  const guess = midnight - offsetNow;
  const atGuess = clubNowIn(now.zone, guess);
  const offsetThen = wallMsOf(atGuess.wall) - guess;
  let boundary = guess;
  if (offsetThen !== offsetNow) {
    const corrected = midnight - offsetThen;
    const changed = clubNowIn(now.zone, corrected).today !== now.today;
    if (corrected > now.instantMs && changed) boundary = corrected;
  }
  return Math.max(1, boundary - now.instantMs);
}
